import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label } from 'reactstrap';
import ReactDatePicker from 'react-datepicker';

import translations from 'services/translations/translations';
import useDateLocale from 'services/translations/useDateLocale';

import 'style/layout/form/Datepicker.css';

const DatePicker = ({
  className,
  disabled,
  endDate,
  id,
  isClearable,
  label,
  labelClassName,
  locale,
  minDate,
  onChange,
  popperContainer,
  popperPlacement,
  required,
  selected,
  // Every calendar shows the week numbers unless told otherwise
  showWeekNumbers = true,
  startDate,
}: {
  className?: string;
  disabled?: boolean;
  endDate?: Date | null;
  id?: string;
  // The date can be emptied with a cross in the field
  isClearable?: boolean;
  label?: string;
  labelClassName?: string;
  locale?: string | Locale;
  // The earliest date that can be chosen
  minDate?: Date;
  onChange(
    date: Date | [Date, Date] | /* for selectsRange */ null,
    event: React.SyntheticEvent<any> | undefined
  ): void;
  // Where the calendar is rendered, e.g. outside a table that would clip it
  popperContainer?(props: { children: React.ReactNode[] }): React.ReactNode;
  // E.g. 'bottom-end' for a field at the right edge of the page
  popperPlacement?: string;
  required?: boolean;
  selected?: Date | null;
  showWeekNumbers?: boolean;
  startDate?: Date | null;
}): JSX.Element => {
  const dateLocale = useDateLocale();
  const { t } = useTranslation([translations.common]);

  const getClassName = () => {
    const classes = ['text-field'];

    if (className !== undefined) {
      classes.push(className);
    }

    return classes.join(' ');
  };

  return (
    <FormGroup>
      {label === undefined ? null : (
        <Label className={`${labelClassName} label`} for={id}>
          {label}
        </Label>
      )}
      <div className="date-picker">
        <ReactDatePicker
          calendarClassName={showWeekNumbers ? 'with-week-numbers' : undefined}
          className={getClassName()}
          dateFormat={dateLocale.dateFormat}
          disabled={disabled}
          endDate={endDate}
          id={id}
          isClearable={isClearable}
          locale={locale ?? dateLocale.datePicker}
          minDate={minDate}
          onChange={onChange}
          popperContainer={popperContainer}
          popperPlacement={popperPlacement}
          required={required}
          selected={selected}
          showWeekNumbers={showWeekNumbers}
          startDate={startDate}
          // The heading of the column of the week numbers, e.g. "Vk"
          weekLabel={t(`${translations.common}:Week short`)}
        />
      </div>
    </FormGroup>
  );
};

export default DatePicker;
